import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { RootState } from ".";

// Types
export interface NotificationItem {
  id: string;
  timestamp: number;
  isRead: boolean;
  // Context
  page: string;
  action: string;
  // Result
  success: boolean;
  message: string;
  details?: unknown;
}

interface NotificationDrawerState {
  isOpen: boolean;
  items: NotificationItem[];
  currentOrg: string;
}

// Initial state
const initialState: NotificationDrawerState = {
  isOpen: false,
  items: [],
  currentOrg: "",
};

// LocalStorage helpers
const STORAGE_KEY_PREFIX = "cpln_notifications_";

export function getStorageKey(org: string): string {
  return `${STORAGE_KEY_PREFIX}${org}`;
}

export function loadNotificationsFromStorage(org: string): NotificationItem[] {
  if (!org) return [];
  try {
    const stored = localStorage.getItem(getStorageKey(org));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to load notifications from localStorage:", e);
  }
  return [];
}

export function saveNotificationsToStorage(org: string, items: NotificationItem[]): void {
  if (!org) return;
  try {
    localStorage.setItem(getStorageKey(org), JSON.stringify(items));
  } catch (e) {
    console.error("Failed to save notifications to localStorage:", e);
  }
}

// Slice
export const notificationDrawerSlice = createSlice({
  name: "notificationDrawer",
  initialState,
  reducers: {
    openDrawer: (state) => {
      state.isOpen = true;
      // Mark all items as read when drawer opens
      for (const item of state.items) {
        item.isRead = true;
      }
      saveNotificationsToStorage(state.currentOrg, state.items);
    },

    closeDrawer: (state) => {
      state.isOpen = false;
    },

    toggleDrawer: (state) => {
      state.isOpen = !state.isOpen;
      // Mark all items as read when drawer opens
      if (state.isOpen) {
        for (const item of state.items) {
          item.isRead = true;
        }
        saveNotificationsToStorage(state.currentOrg, state.items);
      }
    },

    addNotification: (
      state,
      action: PayloadAction<Omit<NotificationItem, "id" | "timestamp" | "isRead">>
    ) => {
      const notification: NotificationItem = {
        ...action.payload,
        id: uuidv4(),
        timestamp: Date.now(),
        isRead: false,
      };
      // Add to beginning (newest first)
      state.items.unshift(notification);
      saveNotificationsToStorage(state.currentOrg, state.items);
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      saveNotificationsToStorage(state.currentOrg, state.items);
    },

    clearAll: (state) => {
      state.items = [];
      saveNotificationsToStorage(state.currentOrg, state.items);
    },

    // Set the current org and load notifications from localStorage
    setNotificationOrg: (state, action: PayloadAction<string>) => {
      const org = action.payload;
      if (state.currentOrg !== org) {
        state.currentOrg = org;
        state.items = loadNotificationsFromStorage(org);
      }
    },

    // Sync notifications from localStorage (called when storage event fires from another tab)
    syncFromStorage: (state) => {
      if (state.currentOrg) {
        state.items = loadNotificationsFromStorage(state.currentOrg);
      }
    },
  },
});

// Actions
export const {
  openDrawer,
  closeDrawer,
  toggleDrawer,
  addNotification,
  removeNotification,
  clearAll,
  setNotificationOrg,
  syncFromStorage,
} = notificationDrawerSlice.actions;

// Selectors
export const selectIsNotificationDrawerOpen = (state: RootState) => state.notificationDrawer.isOpen;
export const selectNotifications = (state: RootState) => state.notificationDrawer.items;
export const selectUnreadCount = (state: RootState) =>
  state.notificationDrawer.items.filter((item: NotificationItem) => !item.isRead).length;

// Reducer
export const notificationDrawerReducer = notificationDrawerSlice.reducer;
